import * as yup from 'yup';
import { memo } from 'react';
import { useFormik } from 'formik';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { SecretField, AlertBoxMsg } from '@qrios/frontend-library';

import locales from '../../../../../../constants/locales';
import Typography from '../../../../../common/typography';
import { WithLoader } from '../../../../../common/loader';
import { OTP_VERIFICATION } from '../../../../../../constants/formInputs';
import { otpSmsValidation } from '../../../../../../constants/validation';

import { StepProps } from '../interfaces';
import StepperFooter from '../stepperFooter';
import { useVerifyOtp, useReSendOtp } from '../hooks';

const useStyles = makeStyles((theme: Theme) => ({
  resentOTP: {
    cursor: 'pointer',
    marginTop: '2px',
    color: theme.palette.primary.main,
    lineHeight: '14px',
    '&:hover': {
      textDecoration: 'underline'
    }
  }
}));

export const validationSchema = yup.object().shape({
  [OTP_VERIFICATION.SMS]: otpSmsValidation
});

const VerifyOtp = ({ goToNextStep, onFailure, msisdn, shortcode }: StepProps) => {
  const classes = useStyles();

  const { verifyOtpError, verifyOtpLoading, handleVerifyOtp } = useVerifyOtp();
  const { reSendOtpError, handleReSendOtp, reSendOtpLoading, resendMsisdn, resendShortcode } = useReSendOtp();

  const formik = useFormik({
    initialValues: {
      [OTP_VERIFICATION.SMS]: ''
    },
    validationSchema,
    onSubmit: otp => {
      handleVerifyOtp({ otpData: { otp }, onSuccess: goToNextStep, onFailure });
    }
  });

  const handleReSendOtpSubmit = () => {
    handleReSendOtp({ onFailure });
  };

  const getActualShortcode = () => {
    if (resendShortcode) return resendShortcode;
    return shortcode;
  };

  const getActualMsisdn = () => {
    if (resendMsisdn) return resendMsisdn;
    return msisdn;
  };

  return (
    <Box pt={2}>
      <WithLoader loading={reSendOtpLoading}>
        {verifyOtpError ? (
          <Box pb={2}>
            <AlertBoxMsg severity={verifyOtpError.severity}>{verifyOtpError.message}</AlertBoxMsg>
          </Box>
        ) : null}
        {reSendOtpError ? (
          <Box pb={2}>
            <AlertBoxMsg severity={reSendOtpError.severity}>{reSendOtpError.message}</AlertBoxMsg>
          </Box>
        ) : null}
        <Box>
          {msisdn || resendMsisdn ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '16px' }}>
              <Typography color="#4A4A4A" size={14}>
                Enter the OTP you recived to &nbsp;
              </Typography>
              <Typography size={14}>{getActualMsisdn()}</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '16px' }}>
              <Typography color="#4A4A4A" size={14}>
                Enter the OTP you recived to your phone
              </Typography>
            </Box>
          )}
          <SecretField
            name={OTP_VERIFICATION.SMS}
            value={formik.values[OTP_VERIFICATION.SMS]}
            handleChange={formik.handleChange}
            handleBlur={formik.handleBlur}
            label={locales.otp.label.sms}
            error={formik.errors[OTP_VERIFICATION.SMS]}
            touched={formik.touched[OTP_VERIFICATION.SMS]}
            placeholder={locales.otp.placeholder.sms}
          />

          {shortcode || resendShortcode ? (
            <Box>
              <Box sx={{ display: 'flex' }}>
                <Typography size={14} color="#4A4A4A">
                  If you didn&#39;t receive the previous OTP on your phone, you can either request a &nbsp;
                </Typography>
                <Box className={classes.resentOTP} onClick={handleReSendOtpSubmit}>
                  Resend
                </Box>
              </Box>
              <Box sx={{ display: 'flex', mt: '2px' }}>
                <Box sx={{ display: 'flex' }}>
                  <Typography size={14} color="#4A4A4A">
                    or call &nbsp;
                  </Typography>
                  <Typography size={14} color="#00683E">
                    {getActualShortcode()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex' }}>
              <Typography size={14} color="#4A4A4A">
                If you didn&#39;t receive the previous OTP on your phone, you can either request a &nbsp;
              </Typography>
              <Box className={classes.resentOTP} onClick={handleReSendOtpSubmit}>
                Resend
              </Box>
            </Box>
          )}
        </Box>
      </WithLoader>
      <StepperFooter
        primaryButton={{
          text: 'Verify OTP',
          isLoading: verifyOtpLoading,
          onClickHandler: formik.handleSubmit
        }}
      />
    </Box>
  );
};

export default memo(VerifyOtp);
